import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAdminCompanies } from "../../api/admin";
import { useCreateFirmware, useActivateFirmware, useFirmware, useFirmwareList, useUpdateFirmware, uploadToS3, computeSHA256 } from "../../api/firmware";
import { useDeviceModels } from "../../api/deviceTypes";
import { extractErrorMessage } from "../../api/errors";
import { getActingCompanyName, getActingTenantId, getRole } from "../../auth/session";

type Step = "metadata" | "uploading" | "confirming" | "done";
type FileSource = "existing" | "upload";

const semverPattern = /^\d+\.\d+\.\d+(-[a-zA-Z0-9.]+)?$/;
const statusOptions = [
  { value: "draft", label: "Draft" },
  { value: "active", label: "Active" },
  { value: "deprecated", label: "Deprecated" },
];

function editableStatusOptions(status: string) {
  if (status === "draft") return statusOptions.filter((option) => ["draft", "active"].includes(option.value));
  if (status === "active") return statusOptions.filter((option) => ["active", "deprecated"].includes(option.value));
  if (status === "deprecated") return statusOptions.filter((option) => ["deprecated", "active"].includes(option.value));
  return statusOptions;
}

export function FirmwareUploadPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isEditing = Boolean(id);
  const requestedTenantId = searchParams.get("tenantId")?.trim() || undefined;
  const scopeTenantId = requestedTenantId ?? getActingTenantId() ?? "";
  const createFirmware = useCreateFirmware(scopeTenantId || undefined);
  const activateFirmware = useActivateFirmware(scopeTenantId || undefined);
  const updateFirmware = useUpdateFirmware(scopeTenantId || undefined);
  const { data: existingFirmware, isLoading } = useFirmware(id ?? "", scopeTenantId || undefined);
  const { data: firmwareList, isLoading: firmwareListLoading } = useFirmwareList({ status: "active", allPages: true, tenantId: scopeTenantId || undefined });
  const isSuperAdmin = getRole() === "super_admin";
  const scopeCompanyName = getActingCompanyName() ?? "";
  const companiesQuery = useAdminCompanies({ enabled: isSuperAdmin && !!scopeTenantId && !scopeCompanyName });
  const resolvedScopeCompanyName = scopeCompanyName || companiesQuery.data?.data.find((company) => company.tenantId === scopeTenantId)?.companyName || "";
  const scopeLabel = scopeTenantId ? resolvedScopeCompanyName || "Selected company files" : "Platform admin files";
  const targetImeis = (searchParams.get("imeis") ?? "")
    .split(",")
    .map((value) => value.trim())
    .filter(Boolean);
  const canUseExistingFile = targetImeis.length > 0 && !isEditing;
  const availableFirmware = useMemo(() => firmwareList?.data ?? [], [firmwareList?.data]);
  const modelsQuery = useDeviceModels();
  const deviceModels = useMemo(
    () =>
      [...(modelsQuery.data?.data ?? [])]
        .filter((entry) => entry.status !== "deprecated")
        .sort((a, b) => a.name.localeCompare(b.name)),
    [modelsQuery.data?.data],
  );

  const [step, setStep] = useState<Step>("metadata");
  const [fileSource, setFileSource] = useState<FileSource>(canUseExistingFile ? "existing" : "upload");
  const [selectedExistingFirmwareId, setSelectedExistingFirmwareId] = useState("");
  const [version, setVersion] = useState("");
  const [model, setModel] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [firmwareId, setFirmwareId] = useState("");
  const [error, setError] = useState("");
  const [progress, setProgress] = useState("");
  // A firmware record edited here may carry a model string from before the
  // catalog existed, or one that's since been deprecated/renamed — don't
  // silently drop it from the select, surface it as an explicit "current" option.
  const hasVisibleModelOption = !!model && deviceModels.some((entry) => entry.modelCode === model);

  useEffect(() => {
    const firmware = existingFirmware?.data;
    if (!firmware) return;
    setVersion(firmware.version);
    setModel(firmware.model);
    setDescription(firmware.releaseNotes ?? "");
    setStatus(firmware.status);
    setFirmwareId(firmware.firmwareId);
  }, [existingFirmware]);

  useEffect(() => {
    if (!canUseExistingFile) {
      setFileSource("upload");
    }
  }, [canUseExistingFile]);

  useEffect(() => {
    if (!canUseExistingFile || selectedExistingFirmwareId || availableFirmware.length === 0) return;
    setSelectedExistingFirmwareId(availableFirmware[0].firmwareId);
  }, [availableFirmware, canUseExistingFile, selectedExistingFirmwareId]);

  function navigateToTask(fwId: string) {
    const params = new URLSearchParams({ firmwareId: fwId });
    if (targetImeis.length > 0) params.set("imeis", targetImeis.join(","));
    if (scopeTenantId) params.set("tenantId", scopeTenantId);
    navigate(`/tasks/create?${params.toString()}`);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (canUseExistingFile && fileSource === "existing") return;
    if (!file && !isEditing) return;
    setError("");

    const cleanVersion = version.trim();
    const cleanModel = model.trim();
    const cleanDescription = description.trim();

    if (!semverPattern.test(cleanVersion)) {
      setError("Version must be a semantic version like 3.4.0. Put labels or notes in the description.");
      return;
    }

    try {
      if (isEditing && id) {
        const body: {
          version: string;
          model: string;
          description: string;
          status?: string;
          fileName?: string;
          fileSize?: number;
          contentType?: string;
        } = {
          version: cleanVersion,
          model: cleanModel,
          description: cleanDescription,
        };

        const statusChanged = status && status !== existingFirmware?.data.status;
        if (!file && statusChanged) {
          body.status = status;
        }

        const replacementFile = file;
        if (replacementFile) {
          body.fileName = replacementFile.name;
          body.fileSize = replacementFile.size;
          body.contentType = replacementFile.type || "application/octet-stream";
        }

        setProgress(replacementFile ? "Preparing firmware replacement..." : "Saving firmware details...");
        const res = await updateFirmware.mutateAsync({ id, body });
        setFirmwareId(id);

        if (!replacementFile) {
          setStep("done");
          setProgress("Firmware details updated successfully.");
          return;
        }

        if (!res.data.uploadUrl) {
          throw new Error("Upload URL was not returned for the replacement file.");
        }

        setStep("uploading");
        setProgress("Uploading replacement firmware binary to S3...");
        await uploadToS3(res.data.uploadUrl, replacementFile);

        setStep("confirming");
        setProgress("Computing checksum and saving firmware replacement...");
        const checksum = await computeSHA256(replacementFile);
        await updateFirmware.mutateAsync({
          id,
          body: {
            checksum,
            ...(statusChanged ? { status } : {}),
          },
        });

        setStep("done");
        setProgress("Firmware replacement uploaded successfully.");
        return;
      }

      const uploadFile = file;
      if (!uploadFile) return;

      // Step 1: Create firmware record and get pre-signed upload URL
      setProgress("Creating firmware record...");
      const res = await createFirmware.mutateAsync({
        version: cleanVersion,
        fileName: uploadFile.name,
        fileSize: uploadFile.size,
        contentType: uploadFile.type || "application/octet-stream",
        description: cleanDescription,
        model: cleanModel,
      });

      const fw = res.data;
      setFirmwareId(fw.firmwareId);
      setStep("uploading");

      // Step 2: Upload binary directly to S3 via pre-signed URL
      setProgress("Uploading firmware binary to S3...");
      await uploadToS3(fw.uploadUrl, uploadFile);

      setStep("confirming");

      // Step 3: Compute checksum and activate (draft → active)
      setProgress("Computing checksum and activating firmware...");
      const checksum = await computeSHA256(uploadFile);
      await activateFirmware.mutateAsync({
        id: fw.firmwareId,
        checksum,
      });

      setStep("done");
      setProgress("Firmware uploaded and activated successfully.");
    } catch (err) {
      setError(extractErrorMessage(err, "Upload failed"));
    }
  }

  if (step === "done") {
    const heading = isEditing ? "Edit Complete" : "Upload Complete";
    return (
      <div className="rounded-lg border border-green-200 bg-green-50 p-6 text-center dark:border-green-800 dark:bg-green-950/30">
        <h2 className="mb-2 text-lg font-semibold text-green-800 dark:text-green-300">{heading}</h2>
        <p className="mb-4 text-sm text-green-700 dark:text-green-400">{progress}</p>
        <div className="flex justify-center gap-3">
          <button onClick={() => navigateToTask(firmwareId)} className="rounded bg-riot-gold-400 px-4 py-2 text-sm font-medium text-riot-navy-900 hover:bg-riot-gold-500 active:bg-riot-gold-600">
            {targetImeis.length > 0 ? "Create rollout for selected devices" : "Create rollout task"}
          </button>
          <button onClick={() => navigate("/firmware")} className="rounded border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50 dark:hover:bg-riot-navy-700">
            Back to List
          </button>
        </div>
      </div>
    );
  }

  if (isEditing && isLoading) {
    return <div className="py-20 text-center text-sm text-gray-400 dark:text-riot-navy-400">Loading file...</div>;
  }

  const selectedStatusOptions = editableStatusOptions(existingFirmware?.data.status ?? status);
  const submitDisabled = !version || !model || (!file && !isEditing) || createFirmware.isPending || updateFirmware.isPending || activateFirmware.isPending;
  const submitLabel = isEditing ? (file ? "Upload replacement" : "Save changes") : "Upload";

  return (
    <div>
      <button onClick={() => navigate("/firmware")} className="mb-4 text-sm text-riot-gold-500 hover:underline">
        &larr; Back to firmware
      </button>

      {isSuperAdmin && (
        <div className="mb-4 rounded-lg border border-riot-gold-500/30 bg-riot-gold-500/10 p-4">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-riot-gold-500">File scope</p>
          <p className="mt-1 text-sm text-gray-700 dark:text-riot-navy-100">
            {isEditing ? "Editing" : "Uploading"} in <span className="font-semibold text-gray-900 dark:text-white">{scopeLabel}</span>.
          </p>
        </div>
      )}

      {targetImeis.length > 0 && (
        <div className="mb-4 rounded-lg border border-gray-200 bg-white p-4 dark:border-riot-navy-700 dark:bg-riot-navy-800">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-riot-gold-500">Selected devices</p>
          <p className="mt-1 text-sm text-gray-700 dark:text-riot-navy-100">
            Choose an existing active file or upload a new one. The rollout task will target {targetImeis.length} selected device{targetImeis.length === 1 ? "" : "s"}.
          </p>
        </div>
      )}

      {canUseExistingFile && step === "metadata" && (
        <div className="mb-4 rounded-lg border border-gray-200 bg-white p-4 dark:border-riot-navy-700 dark:bg-riot-navy-800">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-riot-gold-500">File source</p>
          <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
            <button
              type="button"
              aria-pressed={fileSource === "existing"}
              onClick={() => setFileSource("existing")}
              className={`rounded-md border px-3 py-2 text-left text-sm font-medium ${fileSource === "existing" ? "border-riot-gold-400 bg-riot-gold-400 text-riot-navy-950" : "border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-riot-navy-600 dark:text-riot-navy-100 dark:hover:bg-riot-navy-700"}`}
            >
              Choose existing file
            </button>
            <button
              type="button"
              aria-pressed={fileSource === "upload"}
              onClick={() => setFileSource("upload")}
              className={`rounded-md border px-3 py-2 text-left text-sm font-medium ${fileSource === "upload" ? "border-riot-gold-400 bg-riot-gold-400 text-riot-navy-950" : "border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-riot-navy-600 dark:text-riot-navy-100 dark:hover:bg-riot-navy-700"}`}
            >
              Upload new file
            </button>
          </div>

          {fileSource === "existing" && (
            <div className="mt-4 space-y-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-riot-navy-200">Existing file</label>
                <select
                  value={selectedExistingFirmwareId}
                  onChange={(event) => setSelectedExistingFirmwareId(event.target.value)}
                  className="w-full rounded border border-gray-300 px-3 py-2 text-sm dark:border-riot-navy-600 dark:bg-riot-navy-800 dark:text-white"
                >
                  <option value="">{firmwareListLoading ? "Loading files..." : "Select file..."}</option>
                  {availableFirmware.map((firmware) => (
                    <option key={firmware.firmwareId} value={firmware.firmwareId}>
                      {firmware.version} - {firmware.model} ({firmware.filename})
                    </option>
                  ))}
                </select>
                {!firmwareListLoading && availableFirmware.length === 0 && (
                  <p className="mt-1 text-xs text-gray-500 dark:text-riot-navy-300">
                    No active files are available in this scope. Upload a new file instead.
                  </p>
                )}
              </div>
              <button
                type="button"
                disabled={!selectedExistingFirmwareId}
                onClick={() => navigateToTask(selectedExistingFirmwareId)}
                className="rounded bg-riot-gold-400 px-4 py-2 text-sm font-medium text-riot-navy-900 hover:bg-riot-gold-500 active:bg-riot-gold-600 disabled:opacity-50"
              >
                Use selected file
              </button>
            </div>
          )}
        </div>
      )}

      {(!canUseExistingFile || fileSource === "upload" || step !== "metadata") && (
      <div className="rounded-lg border border-gray-200 bg-white dark:border-riot-navy-700 dark:bg-riot-navy-800 p-6">
        <h2 className="mb-1 text-lg font-semibold">{isEditing ? "Edit Firmware" : canUseExistingFile ? "Upload New Firmware" : "Upload Firmware"}</h2>
        {isEditing && (
          <p className="mb-4 text-sm text-gray-500 dark:text-riot-navy-300">
            {existingFirmware?.data.filename}
          </p>
        )}

        {/* Progress indicator */}
        <div className="mb-6 flex gap-2 text-xs">
          {(["metadata", "uploading", "confirming"] as Step[]).map((s, i) => (
            <span key={s} className={`rounded px-2 py-1 ${step === s ? "bg-riot-gold-100 text-riot-gold-600 font-medium dark:bg-riot-gold-500/20 dark:text-riot-gold-400" : "bg-gray-100 text-gray-400 dark:bg-riot-navy-700 dark:text-riot-navy-400"}`}>
              {i + 1}. {s === "metadata" ? "Details" : s === "uploading" ? "Upload" : "Confirm"}
            </span>
          ))}
        </div>

        {step === "metadata" && (
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-riot-navy-200">Version</label>
              <input
                type="text"
                value={version}
                onChange={(e) => setVersion(e.target.value)}
                placeholder="e.g. 3.4.0"
                pattern="^\d+\.\d+\.\d+(-[a-zA-Z0-9.]+)?$"
                title="Use a semantic version like 3.4.0"
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm dark:border-riot-navy-600 dark:bg-riot-navy-800 dark:text-white"
                required
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-riot-navy-200">Model</label>
              <select
                aria-label="Model"
                value={model}
                onChange={(e) => setModel(e.target.value)}
                disabled={modelsQuery.isLoading}
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm dark:border-riot-navy-600 dark:bg-riot-navy-800 dark:text-white disabled:cursor-not-allowed disabled:opacity-60"
                required
              >
                <option value="">{modelsQuery.isLoading ? "Loading models..." : "Select model..."}</option>
                {model && !hasVisibleModelOption && <option value={model}>{model} (current)</option>}
                {deviceModels.map((entry) => (
                  <option key={entry.modelCode} value={entry.modelCode}>{entry.name}</option>
                ))}
              </select>
            </div>
            {isEditing && (
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-riot-navy-200">Status</label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="w-full rounded border border-gray-300 px-3 py-2 text-sm dark:border-riot-navy-600 dark:bg-riot-navy-800 dark:text-white"
                >
                  {selectedStatusOptions.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-riot-navy-200">Firmware Binary</label>
              <input aria-label="Firmware Binary" type="file" onChange={(e) => setFile(e.target.files?.[0] ?? null)} className="w-full text-sm" accept=".bin,.hex,.elf" required={!isEditing} />
              {isEditing && !file && (
                <p className="mt-1 text-xs text-gray-500 dark:text-riot-navy-300">
                  Leave empty to keep the current binary.
                </p>
              )}
              {file && <p className="mt-1 text-xs text-gray-500 dark:text-riot-navy-300">{file.name} ({(file.size / 1024).toFixed(1)} KB)</p>}
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-riot-navy-200">Description</label>
              <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} className="w-full rounded border border-gray-300 px-3 py-2 text-sm dark:border-riot-navy-600 dark:bg-riot-navy-800 dark:text-white" placeholder="Release notes (optional)" />
            </div>
            <button type="submit" disabled={submitDisabled} className="rounded bg-riot-gold-400 px-6 py-2 text-sm font-medium text-riot-navy-900 hover:bg-riot-gold-500 active:bg-riot-gold-600 disabled:opacity-50">
              {submitLabel}
            </button>
          </form>
        )}

        {(step === "uploading" || step === "confirming") && (
          <div className="py-8 text-center">
            <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-4 border-blue-200 border-t-riot-gold-500" />
            <p className="text-sm text-gray-600 dark:text-riot-navy-300">{progress}</p>
          </div>
        )}

        {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
      </div>
      )}
    </div>
  );
}
